import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { quotes as api, http, getSettings, sendQuoteEmail, apiError, quotePdfUrl } from "@/lib/api";
import { money } from "@/lib/format";
import { Loading } from "@/components/Shared";
import { DocumentA4 } from "@/components/DocumentA4";
import { ShareButton } from "@/components/ShareButton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Printer, Mail, MessageCircle, Pencil, CheckCircle2, XCircle, FileText, Send } from "lucide-react";

export default function QuoteView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [settings, setSettings] = useState(null);

  const load = () => Promise.all([api.get(id), getSettings()]).then(([d, s]) => { setDoc(d); setSettings(s); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [id]);

  if (!doc) return <Loading />;

  const waMsg = encodeURIComponent(`Hi ${doc.customer?.name || ""}, please find your quotation ${doc.number} from ${settings?.company_name || "WiseAce"} for ${money(doc.total)}.`);
  const waNumber = (doc.customer?.phone || settings?.whatsapp || "").replace(/\D/g, "");
  const mailto = `mailto:${doc.customer?.email || ""}?subject=${encodeURIComponent(`Quotation ${doc.number}`)}&body=${waMsg}`;

  const setStatus = async (status) => { try { await http.put(`/quotes/${id}/status`, { status }); toast.success(`Quote ${status}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const convert = async () => {
    try { const inv = await http.post(`/quotes/${id}/convert`).then((r) => r.data); toast.success(`Converted to invoice ${inv.number}`); nav(`/invoices/${inv.id}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 no-print">
        <button onClick={() => nav("/quotes")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0B132B]" data-testid="quote-view-back"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="flex flex-wrap gap-2">
          <a href={quotePdfUrl(id)} download data-testid="print-quote"><Button variant="outline"><Printer className="w-4 h-4 mr-1.5" /> Download PDF</Button></a>
          <ShareButton docType="quote" docId={id} />
          <a href={mailto} data-testid="email-quote"><Button variant="outline"><Mail className="w-4 h-4 mr-1.5" /> Email</Button></a>
          <Button variant="outline" onClick={async () => { try { const r = await sendQuoteEmail(id); toast.success(r.message || "Quote email sent"); } catch (e) { toast.error(apiError(e)); } }} data-testid="send-quote-email"><Send className="w-4 h-4 mr-1.5" /> Send via WiseAce</Button>
          {waNumber && <a href={`https://wa.me/27${waNumber.replace(/^0/, "")}?text=${waMsg}`} target="_blank" rel="noreferrer" data-testid="whatsapp-quote"><Button variant="outline" className="text-emerald-600 border-emerald-200"><MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp</Button></a>}
          <Button variant="outline" onClick={() => nav(`/quotes/${id}/edit`)} data-testid="edit-quote-view"><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>
          {doc.status === "pending" && <>
            <Button variant="outline" className="text-emerald-600 border-emerald-200" onClick={() => setStatus("accepted")} data-testid="accept-quote"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Accept</Button>
            <Button variant="outline" className="text-rose-600 border-rose-200" onClick={() => setStatus("rejected")} data-testid="reject-quote"><XCircle className="w-4 h-4 mr-1.5" /> Reject</Button>
          </>}
          <Button onClick={convert} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="convert-quote"><FileText className="w-4 h-4 mr-1.5" /> Convert to Invoice</Button>
        </div>
      </div>
      <DocumentA4 doc={doc} settings={settings} title="QUOTE" statusValue={doc.status} />
    </div>
  );
}
